import { getSessionToken } from "./sessionToken";

const API_BASE_URL = process.env.REACT_APP_API_URL;

export class ApiError extends Error {
  status: number;
  data: unknown;

  constructor(message: string, status: number, data: unknown = null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

const getAuthToken = (): string | null => getSessionToken();

const toReadableErrorMessage = (value: unknown): string | null => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const parsed = toReadableErrorMessage(item);
      if (parsed) {
        return parsed;
      }
    }
    return null;
  }

  if (typeof value === "object") {
    const asRecord = value as Record<string, unknown>;
    const candidates = [
      asRecord.message,
      asRecord.error,
      asRecord.details,
      asRecord.detail,
      asRecord.reason,
    ];

    for (const candidate of candidates) {
      const parsed = toReadableErrorMessage(candidate);
      if (parsed) {
        return parsed;
      }
    }

    try {
      const serialized = JSON.stringify(value);
      return serialized && serialized !== "{}" ? serialized : null;
    } catch {
      return null;
    }
  }

  return null;
};

interface BuildHeadersOptions {
  headers?: Record<string, string>;
  auth?: boolean;
}

const buildHeaders = (options: BuildHeadersOptions = {}): Record<string, string> => {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...options.headers,
  };

  if (options.auth !== false) {
    const token = getAuthToken();
    if (token) {
      headers.token = token;
    }
  }

  return headers;
};

interface ApiClientOptions extends Omit<RequestInit, "body" | "headers" | "method"> {
  method?: string;
  body?: unknown;
  auth?: boolean;
  headers?: Record<string, string>;
}

export const apiClient = async <T = unknown>(
  endpoint: string,
  options: ApiClientOptions = {}
): Promise<T> => {
  const { method = "GET", body, auth = true, headers: customHeaders, ...rest } = options;

  const config: RequestInit = {
    method,
    headers: buildHeaders({ headers: customHeaders, auth }),
    ...rest,
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  const url = `${API_BASE_URL}${endpoint}`;

  try {
    const response = await fetch(url, config);

    let data: T;
    const contentType = response.headers.get("content-type");

    if (contentType && contentType.includes("application/json")) {
      data = await response.json();
    } else {
      data = (await response.text()) as unknown as T;
    }

    if (!response.ok) {
      const extractedMessage = toReadableErrorMessage(data);
      throw new ApiError(
        extractedMessage || `Request failed with status ${response.status}`,
        response.status,
        data
      );
    }

    return data;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    throw new ApiError((error as Error).message || "An unexpected error occurred", 0, null);
  }
};

export const api = {
  get: <T = unknown>(endpoint: string, options: ApiClientOptions = {}) =>
    apiClient<T>(endpoint, { ...options, method: "GET" }),

  post: <T = unknown>(endpoint: string, body?: unknown, options: ApiClientOptions = {}) =>
    apiClient<T>(endpoint, { ...options, method: "POST", body }),

  put: <T = unknown>(endpoint: string, body?: unknown, options: ApiClientOptions = {}) =>
    apiClient<T>(endpoint, { ...options, method: "PUT", body }),

  patch: <T = unknown>(endpoint: string, body?: unknown, options: ApiClientOptions = {}) =>
    apiClient<T>(endpoint, { ...options, method: "PATCH", body }),

  delete: <T = unknown>(endpoint: string, options: ApiClientOptions = {}) =>
    apiClient<T>(endpoint, { ...options, method: "DELETE" }),
};

export default api;
